import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Sparkles, Trophy, Share2, ArrowRight, MapPin, Loader2, Compass, Leaf } from 'lucide-react';
import { RARITY_UI } from '../lib/rarity';
import RarityBadge from '../components/RarityBadge';
import CinematicBackground from '../components/CinematicBackground';

// 30-day referral cookie — the backend consumes `wq_ref` on registration.
function stashRefCookie(ref) {
  if (!ref) return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `wq_ref=${encodeURIComponent(ref)}; Max-Age=2592000; Path=/; SameSite=Lax${secure}`;
}

/**
 * Public-facing discovery landing page. Anyone with the URL can view.
 * Server-side privacy filtering ensures no precise GPS is exposed.
 */
export default function PublicDiscovery() {
  const { obsId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Capture referral attribution on landing. Priority:
  //   1) ?ref=<user_id> in the URL
  //   2) once the discovery loads, fall back to the sharer (data.user.user_id)
  // Whichever wins is written to the wq_ref cookie for 30 days so that a
  // later sign-up on the same device still credits the correct explorer.
  useEffect(() => {
    const explicit = params.get('ref');
    if (explicit) stashRefCookie(explicit);
  }, [params]);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    axios.get(`${base}/api/public/discovery/${obsId}`)
      .then(r => {
        setData(r.data);
        if (!params.get('ref')) stashRefCookie(r.data?.user?.user_id);
      })
      .catch(err => setError(err.response?.status === 403 ? 'private' : 'not_found'));
  }, [obsId, params]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#040A08] text-foreground flex items-center justify-center p-6" data-testid="public-discovery-error">
        <div className="max-w-md text-center space-y-4">
          <Sparkles className="w-10 h-10 mx-auto text-muted-foreground" strokeWidth={1.4} />
          <h1 className="font-serif text-2xl">
            {error === 'private' ? 'This discovery is private' : 'Discovery not found'}
          </h1>
          <p className="text-xs text-muted-foreground">
            {error === 'private'
              ? 'The explorer has chosen to keep this find to themselves.'
              : "Looks like this trail's gone cold. But there's a whole wild world to explore."}
          </p>
          <button onClick={() => nav('/')} data-testid="public-discovery-cta"
            className="px-6 py-3 rounded-full bg-primary text-white text-xs uppercase tracking-widest">
            Enter WildQuest
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#040A08] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const ui = RARITY_UI[data.species_rarity] || RARITY_UI.common;

  return (
    <div className="relative min-h-screen bg-[#040A08] text-foreground overflow-x-hidden" data-testid="public-discovery-root">
      <div className="relative h-[55svh] min-h-[400px]">
        <CinematicBackground intensity="full" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full px-6 text-center">
          <motion.p
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="text-[10px] uppercase tracking-[0.4em] text-amber-300"
          >
            {data.world_first ? 'World First' : data.regional_first ? 'Regional First' : 'Wild Discovery'}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="font-serif text-4xl sm:text-6xl mt-2"
            data-testid="public-species-name"
            style={{ color: ui.color }}
          >{data.species_common_name}</motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="italic text-sm text-muted-foreground mt-1"
          >{data.species_scientific_name}</motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.8 }}
            className="mt-6 inline-flex flex-col items-center px-8 py-4 rounded-2xl bg-black/45 backdrop-blur-md border border-white/10"
            style={{ boxShadow: `0 0 60px -20px ${ui.color}` }}
          >
            <p
              className="font-mono text-6xl tabular-nums leading-none"
              style={{ color: ui.color, textShadow: `0 2px 24px ${ui.color}88, 0 0 2px rgba(0,0,0,0.9)` }}
              data-testid="public-score"
            >{data.discovery_score}</p>
            <p className="text-[10px] uppercase tracking-widest text-white/80 mt-2">Discovery Score</p>
          </motion.div>
        </div>
      </div>

      <section className="max-w-md mx-auto px-6 pt-6 pb-16 space-y-6">
        <div className={`rounded-3xl p-5 border ${ui.borderClass}`} style={ui.borderStyle}>
          <div className={`rounded-2xl p-4 ${ui.cardClass}`} style={ui.cardStyle}>
            <div className="flex items-center gap-3">
              <img src={data.user.picture || `https://i.pravatar.cc/64?u=${data.user.user_id}`}
                   alt="" className="w-14 h-14 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Discovered by</p>
                <p className="font-serif text-lg truncate">{data.user.name}</p>
                {data.location && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <MapPin className="w-3 h-3" /> {data.location}
                  </p>
                )}
              </div>
              <RarityBadge rarity={data.species_rarity} size="lg" />
            </div>
            <p className="mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">
              {new Date(data.created_at).toLocaleString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            {(data.world_first || data.regional_first) && (
              <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400/15 border border-yellow-400/40 text-yellow-300 text-[10px] uppercase tracking-widest">
                <Trophy className="w-3 h-3" />
                {data.world_first ? 'World First' : 'Regional First'}
              </div>
            )}
          </div>
        </div>

        {/* Learn about the species + share this discovery */}
        <div className="grid grid-cols-2 gap-2" data-testid="public-discovery-actions">
          {data.species_id && (
            <button
              onClick={() => nav(`/species/${data.species_id}`)}
              data-testid="public-species-detail-btn"
              className="h-10 rounded-full bg-white/5 border border-white/10 text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-white/10">
              <Leaf className="w-3.5 h-3.5" /> Learn about this species
            </button>
          )}
          <button
            onClick={async () => {
              const url = `${window.location.origin}/d/${data.observation_id || obsId}?ref=${data.user?.user_id || ''}`;
              if (navigator.share) {
                try { await navigator.share({ title: `${data.species_common_name} on WildQuest`, url }); return; }
                catch (_e) { /* fallthrough */ }
              }
              try {
                await navigator.clipboard.writeText(url);
                window.alert && window.alert('Share link copied');
              } catch (_e) { /* ignore */ }
            }}
            data-testid="public-discovery-share-btn"
            className="h-10 rounded-full bg-accent/90 text-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-accent">
            <Share2 className="w-3.5 h-3.5" /> Share this discovery
          </button>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent p-6 text-center"
        >
          <Compass className="w-10 h-10 mx-auto text-primary-light mb-3" strokeWidth={1.4} />
          <p className="font-serif text-2xl">Join WildQuest</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
            Begin your own adventure. Discover thousands of species. Build your legacy.
          </p>
          <button onClick={() => nav('/')} data-testid="public-discovery-cta"
            className="mt-4 px-6 py-3 rounded-full bg-primary text-white text-xs font-semibold uppercase tracking-widest inline-flex items-center gap-2">
            Start Exploring <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>

        <p className="text-center text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          A love letter to naturalists · WildQuest
        </p>
      </section>
    </div>
  );
}
